// Detection-first CSV dispatch (Phase 3, Piece A+B — spec §9).
//
// Mirrors _pdf_ingest.ts for the tabular-CSV path. A dropped .csv is classified
// by the structural registry over its header row, then routed:
//
//   ofw-csv      → KNOWN shape (Date,Time,Sender,Message). Auto-map, no column
//                  step. Naive timestamps → the shared tz prompt (Piece C).
//   generic-csv  → the canonical six-column schema (timestamp/sender/recipient/
//                  body/attachments). Parse directly; naive ISO rows → tz prompt.
//   (neither)    → an arbitrary tabular CSV → the column-mapping step (Piece B),
//                  with a saved profile reused when the header signature matches.
//
// This module is the pure decision layer (no React, no DOM) so the branch logic
// is unit-testable. The caller owns the side effects (reading the File, the
// mapping / tz-prompt UI, the upload).

import { buildCsvSample } from "./_contract";
import { classify, poll } from "./_registry";
import { parseCsvText } from "./generic_csv";
import { headerSignature, proposeMapping, type ColumnMapping } from "./_column_mapping";
import { loadProfile } from "./_mapping_profiles";

export type CsvKind = "ofw-csv" | "generic-csv";

/** What the caller should do next with a dropped CSV. */
export type CsvRouteDecision =
  | {
      // KNOWN OFW message CSV — auto-mapped, but always needs a tz (naive).
      kind: "ofw-csv";
      needsTimezone: true;
    }
  | {
      // Canonical six-column schema. May or may not need a tz (depends on
      // whether its ISO timestamps carry an offset — the adapter decides at
      // parse time; the caller checks the resulting stats.needs_timezone).
      kind: "generic-csv";
    }
  | {
      // Arbitrary tabular CSV → column mapping. `proposed` is the fuzzy guess
      // (or a reused saved profile); `headers` + `sampleRows` drive the UI.
      kind: "column-map";
      headers: string[];
      sampleRows: Record<string, string>[];
      /** Total data rows (excludes the header), for the "N messages ready" cue. */
      rowCount: number;
      proposed: ColumnMapping;
      signature: string;
      /** True when `proposed` came from a saved profile (skip the step). */
      fromProfile: boolean;
    };

export interface CsvRouteOptions {
  /** Optional override store for the profile lookup (tests). */
  profileLookup?: (signature: string) => ColumnMapping | null;
}

/**
 * Decide the route for a CSV's text. Pure + deterministic over the header +
 * a small sample of rows.
 */
export function decideCsvRoute(
  text: string,
  opts: CsvRouteOptions = {},
): CsvRouteDecision {
  const parsed = parseCsvText(text);
  // De-dupe headers preserving first occurrence: a CSV can repeat a column
  // name, which would otherwise render two identical, ambiguous dropdown
  // options sharing a React key — and the parsed row-dicts already collapse
  // duplicate keys, so the list must match.
  const headers = [...new Set(parsed.header)];
  const sample = buildCsvSample(headers);
  const candidates = poll(sample);
  const classification = classify(candidates);

  // A clear, auto-tier leader that's a CSV adapter routes straight through.
  if (classification.tier === "auto" && classification.format === "ofw-csv") {
    return { kind: "ofw-csv", needsTimezone: true };
  }
  if (classification.tier === "auto" && classification.format === "generic-csv") {
    return { kind: "generic-csv" };
  }

  // Not claimed by a known CSV adapter → column mapping. Reuse a saved profile
  // for this header signature when present (skip the step); else fuzzy-propose.
  const signature = headerSignature(headers);
  const lookup = opts.profileLookup ?? ((s) => loadProfile(s)?.mapping ?? null);
  const saved = lookup(signature);
  const proposed = saved ?? proposeMapping(headers);
  return {
    kind: "column-map",
    headers,
    sampleRows: parsed.rows.slice(0, 5),
    rowCount: parsed.rows.length,
    proposed,
    signature,
    fromProfile: saved !== null,
  };
}
